import { useState } from "react";
import { ChevronDown, Clock, Flame, Users, Target } from "lucide-react";
import type { GapThread } from "@/lib/reddit-gap.server";

function ageLabel(days: number | null): string {
  if (days == null) return "age unknown";
  if (days < 1) return "posted today";
  if (days < 30) return `posted ${days} day${days === 1 ? "" : "s"} ago`;
  if (days < 365) return `posted ${Math.round(days / 30)} month${Math.round(days / 30) === 1 ? "" : "s"} ago`;
  return `posted ${Math.round(days / 365)}y ago`;
}

function engagementLabel(thread: GapThread): string {
  const parts: string[] = [];
  if (thread.upvotes != null) parts.push(`${thread.upvotes.toLocaleString()} upvotes`);
  if (thread.comments != null) parts.push(`${thread.comments.toLocaleString()} comments`);
  return parts.length ? parts.join(" · ") : "engagement unavailable";
}

function fitLabel(thread: GapThread): string | null {
  const n = thread.signals?.subredditThreads;
  if (n == null) return null;
  return n >= 2
    ? `r/${thread.subreddit} surfaced ${n} threads in this scan`
    : `r/${thread.subreddit} matched once`;
}

function Bar({ label, points, max }: { label: string; points: number; max: number }) {
  const pct = max > 0 ? Math.min(100, Math.round((points / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{label}</span>
        <span className="font-mono">{points.toFixed(1)}</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-border">
        <div className="brand-gradient h-full rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * Makes the ranking legible: the same numbers the scan sorted on, shown as
 * inline tags with an optional expandable weight breakdown. Threads from
 * older scans have no stored signals, so the tags fall back to the raw
 * thread facts and the breakdown is simply hidden.
 */
export function ThreadScoreBreakdown({ thread }: { thread: GapThread }) {
  const [open, setOpen] = useState(false);
  const s = thread.signals;
  const fit = fitLabel(thread);

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="inline-flex items-center gap-1 rounded-md border border-border bg-panel px-1.5 py-0.5 text-ink">
          <Clock className="h-3 w-3 text-muted-foreground" /> {ageLabel(thread.ageDays)}
        </span>
        <span className="inline-flex items-center gap-1 rounded-md border border-border bg-panel px-1.5 py-0.5 text-ink">
          <Flame className="h-3 w-3 text-muted-foreground" /> {engagementLabel(thread)}
        </span>
        {fit && (
          <span className="inline-flex items-center gap-1 rounded-md border border-border bg-panel px-1.5 py-0.5 text-ink">
            <Users className="h-3 w-3 text-muted-foreground" /> {fit}
          </span>
        )}
        {thread.competitors.length > 0 && (
          <span className="inline-flex items-center gap-1 rounded-md border border-border bg-panel px-1.5 py-0.5 text-ink">
            <Target className="h-3 w-3 text-muted-foreground" />
            {thread.competitors.join(", ")} mentioned
          </span>
        )}
        {s && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-muted-foreground hover:text-ink"
          >
            Score {thread.score?.toFixed(1)}
            <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>

      {s && open && (
        <div className="mt-2 grid gap-2 rounded-lg border border-border bg-panel p-3 sm:grid-cols-2">
          <Bar label="Recency" points={s.recency} max={6} />
          <Bar label="Engagement" points={s.engagement} max={5} />
          <Bar label="Subreddit fit" points={s.subredditFit} max={2} />
          <Bar label="Competitor mentions" points={s.competitors} max={6} />
          {s.intent > 0 && (
            <div className="text-[11px] text-muted-foreground sm:col-span-2">
              +{s.intent.toFixed(1)} buyer-intent phrasing in the title
            </div>
          )}
          {s.matchedQuery && (
            <div className="text-[11px] text-muted-foreground sm:col-span-2">
              Found via search: “{s.matchedQuery}”
            </div>
          )}
        </div>
      )}
    </div>
  );
}
